/**
 * Investment List Component
 * Displays and manages investment items with filtering and editing capabilities
 */

import React, { useState, useEffect, useMemo } from "react";
import "./InvestmentList.css";
import InvestmentItem from "./InvestmentItem";
import GSSharesPopup from "./GSSharesPopup";
import SaxoPopup from "./SaxoPopup";
import MonarchPopup from "./MonarchPopup";
import IBPopup from "./IBPopup";
import FidelityPopup from "./FidelityPopup";
import EntityPopup from "./EntityPopup";
import GryKriCaPopup from "./GryKriCaPopup";
import HousePopup from "./HousePopup";
import PatrimoniaPopup from "./PatrimoniaPopup";
import PeFundPopup from "./PeFundPopup";
import MarcJuniorPopup from "./MarcJuniorPopup";
import CashPopup from "./CashPopup";
import { apiGet, apiPut, apiPatch } from "../../utils/api";

function InvestmentList({ 
  investments, 
  setInvestments, 
  onDataUpdate, 
  selectedTypes, 
  setSelectedTypes, 
  allTypes 
}) {
  // Edit state
  const [editableInvestment, setEditableInvestment] = useState(null);
  const [updatedInvestment, setUpdatedInvestment] = useState({});
  const [editMode, setEditMode] = useState(null); // 'amend' | 'updateValue' | null

  // UI state
  const [fxRate, setFxRate] = useState(null);

  // GS Shares popup
  const [showPopup, setShowPopup] = useState(false);
  const [gsShares, setGsShares] = useState([]);

  // Saxo popup
  const [showSaxoPopup, setShowSaxoPopup] = useState(false);
  const [saxoInvestmentName, setSaxoInvestmentName] = useState(null);
  const [saxoData, setSaxoData] = useState(null);
  const [saxoLoading, setSaxoLoading] = useState(false);
  const [saxoError, setSaxoError] = useState(null);

  // Monarch popup
  const [showMonarchPopup, setShowMonarchPopup] = useState(false);

  // Entity (BV) popup — Brutus etc.
  const [entitySlug, setEntitySlug] = useState(null);

  // Fidelity ISA popup
  const [showFidPopup, setShowFidPopup] = useState(false);
  const [fidData, setFidData] = useState(null);
  const [fidLoading, setFidLoading] = useState(false);
  const [fidError, setFidError] = useState(null);

  // IB popup
  const [showIBPopup, setShowIBPopup] = useState(false);
  const [ibData, setIbData] = useState(null);
  const [ibLoading, setIbLoading] = useState(false);
  const [ibError, setIbError] = useState(null);

  // House (Huis) popup — property + mortgage split
  const [showHousePopup, setShowHousePopup] = useState(false);

  // Patrimonia popup — private Patrimmonia Real Estate stake
  const [showPatrimPopup, setShowPatrimPopup] = useState(false);

  // PE fund-of-funds popup (FOM, EverBlue) — driven by peFundDetail
  const [peFundName, setPeFundName] = useState(null);

  // Marc Junior NV popup — 25% family-NV stake
  const [showMarcJuniorPopup, setShowMarcJuniorPopup] = useState(false);

  // Consolidated Cash popup — zero-balance cash accounts grouped under one card
  const [showCashPopup, setShowCashPopup] = useState(false);

  /**
   * Toggle investment type filter
   * @param {string} type - Investment type to toggle
   */
  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type]
    );
  };

  /**
   * Select all investment types
   */
  const selectAll = () => setSelectedTypes(allTypes);

  /**
   * Clear all investment type filters
   */
  const clearAll = () => setSelectedTypes([]);

  /**
   * Handle form input changes during editing
   * @param {Event} e - Input change event
   */
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setUpdatedInvestment((prev) => {
      const next = { ...prev, [name]: value };
      
      // Auto-calculate EUR value if updating value and FX rate is available
      if (name === "value" && fxRate) {
        const numeric = Number(value);
        const fx = Number(fxRate);
        if (!Number.isNaN(numeric) && !Number.isNaN(fx)) {
          next.eurValue = Number((numeric * fx).toFixed(2));
        }
      }
      return next;
    });
  };

  /**
   * Start editing investment metadata
   * @param {Object} investment - Investment to edit
   */
  const handleAmend = (investment) => {
    setEditableInvestment(investment._id);
    setUpdatedInvestment(investment);
    setEditMode("amend");
  };

  /**
   * Start updating investment value
   * @param {Object} investment - Investment to update
   */
  const handleUpdateValue = async (investment) => {
    setEditableInvestment(investment._id);
    setUpdatedInvestment(investment);
    setEditMode("updateValue");

    try {
      // Fetch current FX rate
      const response = await fetch("https://api.fxratesapi.com/latest");
      const data = await response.json();
      const eurRate = data.rates.EUR;
      let rate;

      if (investment.currency === "EUR") {
        rate = 1;
      } else if (investment.currency === "GBP") {
        rate = eurRate / data.rates.GBP;
      } else if (investment.currency === "USD") {
        rate = eurRate;
      } else {
        rate = data.rates[investment.currency] / eurRate;
      }

      setFxRate(Number(rate).toFixed(3));
    } catch (error) {
      console.error("Error fetching FX rate:", error);
    }
  };

  /**
   * Cancel editing mode
   */
  const handleCancel = () => {
    setEditableInvestment(null);
    setFxRate(null);
    setEditMode(null);
    setUpdatedInvestment({});
  };

  // Field whitelists for different edit modes
  const AMEND_FIELDS = [
    "name", "type", "subType", "broker", "currency", 
    "ticker", "isin", "account", "notes", "info"
  ];
  const VALUE_FIELDS = ["value", "eurValue", "timeOfUpdate"];

  /**
   * Update investment data
   * @param {string} id - Investment ID
   */
  const handleUpdate = async (id) => {
    try {
      // Preserve scroll position
      const scrollPosition = window.scrollY;
  
      const isAmend = editMode === "amend";

      // Remove empty values
      const cleaned = Object.fromEntries(
        Object.entries(updatedInvestment).filter(
          ([, v]) => v !== undefined && v !== ""
        )
      );

      // Prepare payload based on edit mode
      let payload = {};
      let endpoint = "";

      if (isAmend) {
        // Update metadata
        AMEND_FIELDS.forEach((key) => {
          if (cleaned[key] !== undefined) payload[key] = cleaned[key];
        });
        endpoint = `/api/investments/${id}`;
        
        const data = await apiPut(endpoint, payload);
        setInvestments((prev) => prev.map((x) => (x._id === id ? data : x)));
      } else {
        // Update values
        VALUE_FIELDS.forEach((key) => {
          if (cleaned[key] !== undefined) payload[key] = cleaned[key];
        });
        
        if (!payload.timeOfUpdate) {
          payload.timeOfUpdate = new Date().toISOString();
        }
        endpoint = `/api/investments/updateValue/${id}`;
        
        const data = await apiPatch(endpoint, payload);
        setInvestments((prev) => prev.map((x) => (x._id === id ? data : x)));
      }

      handleCancel();
      
      // Restore scroll position after a brief delay to allow re-render
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
      
      // Notify parent component of data update
      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error) {
      console.error("Update failed", error);
      alert(`Update failed: ${error.message}`);
    }
  };

  /**
   * Handle investment card click — routes to GS, Saxo, or IB popup by name
   */
  const handleInvestmentClick = async (name) => {
    if (name === 'Monarch Assurance') {
      setShowMonarchPopup(true);
      return;
    }

    if (typeof name === 'string' && name.startsWith('Saxo')) {
      setSaxoInvestmentName(name);
      setSaxoLoading(true);
      setSaxoError(null);
      setSaxoData(null);
      setShowSaxoPopup(true);
      try {
        const response = await apiGet(`/api/saxo/positions?name=${encodeURIComponent(name)}`);
        if (response.success) {
          setSaxoData(response);
        } else {
          setSaxoError(response.message || 'Failed to load Saxo data');
        }
      } catch (err) {
        setSaxoError(err.message || 'Failed to load Saxo data');
      } finally {
        setSaxoLoading(false);
      }
      return;
    }

    if (name === 'Brutus' || name === 'Brutus & Co BV') {
      setEntitySlug('brutus-co-bv');
      return;
    }

    if (name === 'GryKRYCA' || name === 'GryKriCa' || name === 'GryKriCa Comm.V') {
      setEntitySlug('grykrica');
      return;
    }

    if (name === 'Kx Invest' || name === 'KX Invest' || name === 'KX Invest BV') {
      setEntitySlug('kx-invest');
      return;
    }

    if (name === 'Huis') {
      setShowHousePopup(true);
      return;
    }

    if (name === 'Patrimonia') {
      setShowPatrimPopup(true);
      return;
    }

    if (name === 'FOM' || name === 'EverBlue' || name === 'GS StoneBridge' || name === 'Sourdough Sophia') {
      setPeFundName(name);
      return;
    }

    if (name === 'Marc Junior' || name === 'Marc Junior NV') {
      setShowMarcJuniorPopup(true);
      return;
    }

    if (name === 'Fidelity' || name === 'Fidelity ISA') {
      setFidLoading(true);
      setFidError(null);
      setFidData(null);
      setShowFidPopup(true);
      try {
        const response = await apiGet('/api/fidelity/positions');
        if (response.success) {
          setFidData(response);
        } else {
          setFidError(response.message || 'Failed to load Fidelity ISA data');
        }
      } catch (err) {
        setFidError(err.message || 'Failed to load Fidelity ISA data');
      } finally {
        setFidLoading(false);
      }
      return;
    }

    if (name === 'Interactive Brokers') {
      setIbLoading(true);
      setIbError(null);
      setIbData(null);
      setShowIBPopup(true);
      try {
        const response = await apiGet('/api/ib/positions');
        if (response.success) {
          setIbData(response);
        } else {
          setIbError(response.message || 'Failed to load IB data');
        }
      } catch (err) {
        setIbError(err.message || 'Failed to load IB data');
      } finally {
        setIbLoading(false);
      }
      return;
    }

    // Default: GS Shares popup
    try {
      const response = await apiGet('/api/GSShares');
      const gsSharesData = response.data || response;
      setGsShares(gsSharesData);
      setShowPopup(true);
    } catch (error) {
      console.error("Error fetching GS shares:", error);
    }
  };

  /**
   * Close GS shares popup
   */
  const handleClosePopup = () => setShowPopup(false);

  /**
   * Close Saxo popup
   */
  const handleCloseSaxoPopup = () => {
    setShowSaxoPopup(false);
    setSaxoData(null);
    setSaxoError(null);
  };

  /**
   * Close Fidelity popup
   */
  const handleCloseFidPopup = () => {
    setShowFidPopup(false);
    setFidData(null);
    setFidError(null);
  };

  /**
   * Close IB popup
   */
  const handleCloseIBPopup = () => {
    setShowIBPopup(false);
    setIbData(null);
    setIbError(null);
  };

  // Filter and sort investments
  // Only show investments if there are selected types
  const filtered = selectedTypes.length > 0
    ? investments.filter((inv) => selectedTypes.includes(inv.type))
    : [];

  // Zero-balance cash accounts are grouped under one synthetic "Cash" card.
  const isZeroCash = (inv) =>
    inv.type === 'cash' &&
    Math.abs(Number(inv.eurValue) || 0) < 0.5 &&
    Math.abs(Number(inv.value) || 0) < 0.5;
  const zeroCashAccounts = filtered.filter(isZeroCash);

  const sorted = [...filtered]
    .filter((inv) => !isZeroCash(inv))
    .sort((a, b) => (Number(b.eurValue) || 0) - (Number(a.eurValue) || 0));

  const totalEurValue = filtered.reduce(
    (sum, inv) => sum + (Number(inv.eurValue) || 0),
    0
  );

  return (
    <div className="investment-list-container">
      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-buttons">
          <button type="button" onClick={selectAll}>Select All</button>
          <button type="button" onClick={clearAll}>Clear</button>
        </div>
        {allTypes.map((type) => (
          <button
            key={type}
            className={`filter-button ${selectedTypes.includes(type) ? "selected" : ""}`}
            onClick={() => toggleType(type)}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Total Value Display */}
      <div className="total-eur-value">
        <h3>Total EUR Value: €{totalEurValue.toLocaleString()}</h3>
      </div>

      {/* Investment List */}
      <div className="investment-list">
        {sorted.map((investment) => (
          <InvestmentItem
            key={investment._id}
            investment={investment}
            editableInvestment={editableInvestment}
            editMode={editMode}
            updatedInvestment={updatedInvestment}
            fxRate={fxRate}
            handleEditChange={handleEditChange}
            handleAmend={handleAmend}
            handleUpdateValue={handleUpdateValue}
            handleCancel={handleCancel}
            handleUpdate={handleUpdate}
            handleInvestmentClick={handleInvestmentClick}
          />
        ))}

        {/* Consolidated Cash card — zero-balance accounts grouped */}
        {zeroCashAccounts.length > 0 && (
          <div className="investment-item" style={{ borderLeft: '4px solid #F59E0B' }}>
            <div className="inv-header">
              <h3 className="clickable" onClick={() => setShowCashPopup(true)}>Cash</h3>
              <span className="inv-type-badge" style={{ background: '#F59E0B18', color: '#F59E0B' }}>
                cash
              </span>
            </div>
            <div className="inv-subrow">
              <span className="inv-subtype">
                {zeroCashAccounts.length} empty account{zeroCashAccounts.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="inv-info">Zero-balance accounts, grouped — click to expand.</div>
            <div className="inv-value-block">
              <div className="inv-eur-value">€0</div>
            </div>
          </div>
        )}
      </div>

      {/* GS Shares Popup */}
      {showPopup && (
        <GSSharesPopup
          onClose={handleClosePopup}
          gsShares={gsShares}
          investment={investments.find(i => i.name === 'Fidelity GS')}
        />
      )}

      {/* Saxo popup (Kyra / Mama / any 'Saxo *' investment) */}
      {showSaxoPopup && (
        <SaxoPopup
          onClose={handleCloseSaxoPopup}
          data={saxoData}
          loading={saxoLoading}
          error={saxoError}
          investmentName={saxoInvestmentName}
        />
      )}

      {/* Monarch Assurance popup (Carl) */}
      {showMonarchPopup && (
        <MonarchPopup onClose={() => setShowMonarchPopup(false)} />
      )}

      {/* Entity (BV) Popup — GryKriCa gets the property-centric variant */}
      {entitySlug === 'grykrica' && (
        <GryKriCaPopup
          onClose={() => setEntitySlug(null)}
        />
      )}
      {entitySlug && entitySlug !== 'grykrica' && (
        <EntityPopup
          slug={entitySlug}
          onClose={() => setEntitySlug(null)}
        />
      )}

      {/* Fidelity ISA Popup */}
      {showFidPopup && (
        <FidelityPopup
          onClose={handleCloseFidPopup}
          data={fidData}
          loading={fidLoading}
          error={fidError}
        />
      )}

      {/* Interactive Brokers Popup */}
      {showIBPopup && (
        <IBPopup
          onClose={handleCloseIBPopup}
          data={ibData}
          loading={ibLoading}
          error={ibError}
        />
      )}

      {/* House (Huis) Popup — property + mortgage split */}
      {showHousePopup && (
        <HousePopup
          onClose={() => setShowHousePopup(false)}
          investment={investments.find(i => i.name === 'Huis')}
        />
      )}

      {/* Patrimonia Popup — private Patrimmonia Real Estate stake */}
      {showPatrimPopup && (
        <PatrimoniaPopup
          onClose={() => setShowPatrimPopup(false)}
          investment={investments.find(i => i.name === 'Patrimonia')}
        />
      )}

      {/* PE fund-of-funds Popup (FOM, EverBlue) */}
      {peFundName && (
        <PeFundPopup
          onClose={() => setPeFundName(null)}
          investment={investments.find(i => i.name === peFundName)}
        />
      )}

      {/* Marc Junior NV Popup — 25% family-NV stake */}
      {showMarcJuniorPopup && (
        <MarcJuniorPopup
          onClose={() => setShowMarcJuniorPopup(false)}
          investment={investments.find(i => i.name === 'Marc Junior NV' || i.name === 'Marc Junior')}
        />
      )}

      {/* Consolidated Cash Popup — zero-balance cash accounts */}
      {showCashPopup && (
        <CashPopup
          onClose={() => setShowCashPopup(false)}
          accounts={zeroCashAccounts}
          onDataUpdate={onDataUpdate}
        />
      )}
    </div>
  );
}

export default InvestmentList;
